import { useTranslation } from 'react-i18next';
import Free2exLogoIcon from '@/shared/assets/f2ex-logo-14-icon.svg?react';
import { useAppSettingsStore } from '@/shared/lib';
import { cn } from '@/shared/utils';
import {
  useWalletsAssetActions,
  WalletsAssetActionMenu,
  WalletsAssetAmount,
  type WalletAssetRow,
} from '@/widgets/wallet-assets';
import { SEARCH_ACTION_TRIGGER_CLASSNAME } from '../../config/styles';
import { SearchCell } from './SearchCell';
import { SearchGroupTitle } from './SearchGroupTitle';

type Props = {
  rows: Array<WalletAssetRow>;
  accountNumber?: string;
  baseCode: string;
  withTabTitle?: boolean;
  onActionRun: () => void;
};

const SearchWalletsGroup = ({
  rows,
  accountNumber,
  baseCode,
  withTabTitle = false,
  onActionRun,
}: Props) => {
  const { t } = useTranslation();
  const { isBalancesHidden } = useAppSettingsStore();

  const {
    getActionsVisibility,
    onDeposit,
    onWithdraw,
    onExchange,
    onTransfer,
  } = useWalletsAssetActions(accountNumber);

  if (rows.length === 0) {
    return null;
  }

  const title = t('personalAccount.overview.cards.ewallet');

  return (
    <div className={cn('flex flex-col', withTabTitle && 'gap-[1.6rem]')}>
      {withTabTitle ? (
        <span className="flex h-[2.8rem] w-fit items-center gap-[0.8rem] pl-[1rem] pr-[1.2rem] rounded-[1.2rem] bg-white/8 medium5 text-primary">
          <Free2exLogoIcon className="size-[1.4rem] shrink-0" />
          {title}
        </span>
      ) : (
        <SearchGroupTitle>{title}</SearchGroupTitle>
      )}

      <div className="flex flex-col">
        {rows.map(row => (
          <SearchCell
            key={row.code}
            code={row.code}
            name={row.name}
            icon={row.icon}
            amount={
              <>
                <WalletsAssetAmount
                  isBalancesHidden={isBalancesHidden}
                  valueText={row.amountText}
                  suffix={row.code}
                  reservedValue={row.reserved}
                  reservedText={row.reservedText}
                  valueClassName="medium5"
                  reservedClassName="p6"
                />
                <WalletsAssetAmount
                  isBalancesHidden={isBalancesHidden}
                  valueText={row.baseAmountText}
                  suffix={baseCode}
                  reservedValue={row.baseReserved}
                  reservedText={row.baseReservedText}
                  valueClassName="p6 text-secondary"
                  reservedClassName="p6"
                />
              </>
            }
            action={
              <WalletsAssetActionMenu
                row={row}
                visibility={getActionsVisibility(row)}
                contentClassName="dark"
                triggerClassName={SEARCH_ACTION_TRIGGER_CLASSNAME}
                onDeposit={r => {
                  onActionRun();
                  onDeposit(r);
                }}
                onWithdraw={r => {
                  onActionRun();
                  onWithdraw(r);
                }}
                onExchange={r => {
                  onActionRun();
                  onExchange(r);
                }}
                onTransfer={r => {
                  onActionRun();
                  onTransfer(r);
                }}
              />
            }
          />
        ))}
      </div>
    </div>
  );
};

export { SearchWalletsGroup };
